/**
 * Catálogo de relatórios para solicitação assíncrona (scheduler), sem renderizar dados.
 * Entradas com listEndpoint exportável. Plotagem (mapa) permanece de fora.
 */

/** @typedef {'empresa'|'linhas'|'veiculos'|'unidades'|'grupo'|'cerca'|'intervalo'} RelatorioFilter */

/**
 * @typedef {object} RelatorioExtensoEntry
 * @property {string} id
 * @property {string} screen - nome_tela (permissão)
 * @property {string} labelKey - i18n
 * @property {string} listEndpoint - path relativo allowlisted
 * @property {'POST'|'GET'} [listMethod]
 * @property {string|null} [codTabela]
 * @property {boolean} [needsDates]
 * @property {'range'|'single'} [dateMode]
 * @property {RelatorioFilter[]} [filters]
 * @property {RelatorioFilter[]} [requiredFilters] - filtros obrigatórios na UI
 * @property {string} filename
 * @property {string} [paramStyle]
 * @property {boolean} [autoColumns]
 * @property {{ key: string, titleKey?: string, title?: string }[]} [columns]
 * @property {RelatorioExtensoTableVariant[]} [tableVariants]
 */

/**
 * @typedef {object} RelatorioExtensoTableVariant
 * @property {string} id
 * @property {string} labelKey
 * @property {string} listEndpoint
 * @property {'POST'|'GET'} [listMethod]
 * @property {string[]} [pathParams]
 * @property {string} [codTabela]
 * @property {string} filename
 */

/** @type {RelatorioExtensoTableVariant[]} */
export const HISTORICO_VEICULO_TABLE_VARIANTS = [
  {
    id: 'guias',
    labelKey: 'RELHistoricoVeiculo.tabs.guides',
    listEndpoint: 'getguiasveiculodata',
    listMethod: 'POST',
    codTabela: 'RELHISTORICOVEICULO__GUIAS',
    filename: 'historicoveiculo_guias',
  },
  {
    id: 'viagens',
    labelKey: 'RELHistoricoVeiculo.tabs.realizedTrips',
    listEndpoint: 'getviagensgridveiculodata',
    listMethod: 'POST',
    codTabela: 'RELHISTORICOVEICULO__VIAGENS',
    filename: 'historicoveiculo_viagens',
  },
  {
    id: 'bilhetagens',
    labelKey: 'RELHistoricoVeiculo.tabs.bilhetagens',
    listEndpoint: 'getbilhetagensporcarrodata',
    listMethod: 'POST',
    codTabela: 'RELHISTORICOVEICULO__BILHETAGENS',
    filename: 'historicoveiculo_bilhetagens',
  },
  {
    id: 'notificacoes',
    labelKey: 'RELHistoricoVeiculo.tabs.notifications',
    listEndpoint: 'getnotificacoesveiculopordata',
    listMethod: 'GET',
    pathParams: ['dti', 'dtf', 'idveiculo'],
    codTabela: 'RELHISTORICOVEICULO__NOTIFICACOES',
    filename: 'historicoveiculo_notificacoes',
  },
];

/** @type {RelatorioExtensoTableVariant[]} */
export const MANUTENCAO_EQUIPAMENTOS_TABLE_VARIANTS = [
  {
    id: 'validadores',
    labelKey: 'relatoriosExtenso.manutencao.tabs.validadores',
    listEndpoint: 'getequipamentosvalidadores',
    listMethod: 'POST',
    filename: 'relmanutencao_validadores',
    tipoAba: 'validadores',
    tipo: 'comunicacao',
  },
  {
    id: 'rastreadores',
    labelKey: 'relatoriosExtenso.manutencao.tabs.rastreadores',
    listEndpoint: 'getequipamentosrastreadores',
    listMethod: 'POST',
    filename: 'relmanutencao_rastreadores',
    tipoAba: 'rastreadores',
    tipo: 'metricas',
  },
  {
    id: 'validadores_rastreadores_bilhetagem',
    labelKey: 'relatoriosExtenso.manutencao.tabs.validadoresRastreadores',
    listEndpoint: 'getequipamentosvalidadoresrastreadoresbilhetagem',
    listMethod: 'POST',
    filename: 'relmanutencao_validadores_rastreadores_bilhetagem',
    tipoAba: 'validadores_rastreadores_bilhetagem',
    tipo: 'metricas',
  },
  {
    id: 'dvr',
    labelKey: 'relatoriosExtenso.manutencao.tabs.dvr',
    listEndpoint: 'getequipamentosdvr',
    listMethod: 'POST',
    filename: 'relmanutencao_dvr',
    tipoAba: 'dvr',
    tipo: 'comunicacao',
  },
];

/** @type {RelatorioExtensoTableVariant[]} */
export const PASSAGEIROS_VIAGEM_TABLE_VARIANTS = [
  {
    id: 'faixas',
    labelKey: 'relTripPassangers.title',
    listEndpoint: 'getpassageirosporfaixahoraria',
    listMethod: 'POST',
    codTabela: 'RELPASSAGEIROSVIAGEM__FAIXAS_WITH_PROPERTIES',
    filename: 'passageirosporfaixahoraria',
  },
  {
    id: 'viagem',
    labelKey: 'relTripPassangers.title2',
    listEndpoint: 'getprevistorealizadolinhasdata',
    listMethod: 'POST',
    codTabela: 'RELPASSAGEIROSVIAGEM',
    filename: 'passageirosporviagem',
  },
];

/** @type {RelatorioExtensoEntry[]} */
export const RELATORIOS_EXTENSO_CATALOG = [
  {
    id: 'pontualidade',
    screen: 'RELPontualidade',
    labelKey: 'nav.reports.punctuality',
    listEndpoint: 'getrelatoriopontualidade',
    listMethod: 'POST',
    needsDates: true,
    filters: ['empresa', 'linhas', 'veiculos', 'unidades', 'grupo'],
    filename: 'pontualidade',
    paramStyle: 'standard_grupo',
  },
  {
    id: 'atrasoViagens',
    screen: 'RELAtrasoViagens',
    labelKey: 'nav.reports.tripDelay',
    listEndpoint: 'getrelatorioatrasoviagens',
    listMethod: 'POST',
    codTabela: 'RELATRASOVIAGENS',
    needsDates: true,
    filters: ['empresa', 'linhas', 'veiculos', 'unidades'],
    filename: 'atrasoviagens',
    paramStyle: 'standard',
  },
  {
    id: 'ocupacaoTurno',
    screen: 'RELOcupacaoTurno',
    labelKey: 'nav.reports.occupancyByShift',
    listEndpoint: 'getrelatorioocupacaoporturno',
    listMethod: 'POST',
    codTabela: 'RELOCUPACAOTURNO',
    needsDates: true,
    filters: ['linhas', 'unidades'],
    filename: 'ocupacaoporturno',
    paramStyle: 'ocupacao',
  },
  {
    id: 'embarques',
    screen: 'RELEmbarques',
    labelKey: 'nav.reports.boarding',
    listEndpoint: 'getallembarquesdata',
    listMethod: 'POST',
    codTabela: 'RELEMBARQUES',
    needsDates: true,
    filters: ['linhas', 'veiculos', 'unidades'],
    filename: 'embarques',
    paramStyle: 'embarques',
  },
  {
    id: 'diarioBordo',
    screen: 'RELDiarioBordo',
    labelKey: 'nav.reports.logbook',
    listEndpoint: 'getentradaspordata',
    listMethod: 'POST',
    codTabela: 'RELDIARIOBORDO',
    needsDates: true,
    filters: ['linhas', 'veiculos'],
    filename: 'diariobordo',
    paramStyle: 'diario',
  },
  {
    id: 'usuariosPassageiros',
    screen: 'RELUsuariosPassageiros',
    labelKey: 'nav.reports.passengersUsers',
    listEndpoint: 'getusuariospessoas',
    listMethod: 'POST',
    codTabela: 'USUARIOSPASSAGEIROS',
    needsDates: true,
    filters: [],
    filename: 'usuariospassageiros',
    paramStyle: 'usuarios_passageiros',
  },
  {
    id: 'acessoQrCode',
    screen: 'RELAcessoQrCode',
    labelKey: 'nav.reports.qrCodeAccess',
    listEndpoint: 'getstatusqrcode',
    listMethod: 'GET',
    codTabela: 'RELACESSOQRCODE',
    needsDates: true,
    filters: [],
    filename: 'acessoqrcode',
    paramStyle: 'acesso_qrcode',
  },
  {
    id: 'historicoEnvioQrCode',
    screen: 'RELHistoricoEnvioQrCode',
    labelKey: 'nav.reports.qrCodeSendHistory',
    listEndpoint: 'gethistoricoenviosqrcode',
    listMethod: 'GET',
    codTabela: 'RELHISTORICOENVIOQRCODE',
    needsDates: true,
    filters: [],
    filename: 'historicoenvioqrcode',
    paramStyle: 'dti_dtf',
  },
  {
    id: 'atrasoCercas',
    screen: 'RELAtrasoCercas',
    labelKey: 'nav.reports.delayByGeofence',
    listEndpoint: 'getatrasocercasdata',
    listMethod: 'GET',
    codTabela: 'RELATRASOCERCAS',
    needsDates: true,
    filters: ['empresa', 'linhas', 'veiculos', 'unidades'],
    filename: 'atrasosocupacaocercas',
    paramStyle: 'dti_dtf',
  },
  {
    id: 'cumprimentoRota',
    screen: 'RELCumprimentoRota',
    labelKey: 'nav.reports.routeFulfillment',
    listEndpoint: 'getviagenscumprimentoderota',
    listMethod: 'POST',
    codTabela: 'RELCUMPRIMENTOROTA',
    needsDates: true,
    filters: ['empresa', 'linhas', 'veiculos', 'unidades'],
    filename: 'cumprimentorota',
    paramStyle: 'cumprimento',
  },
  {
    id: 'eventosHikvision',
    screen: 'RELEventosHikvision',
    labelKey: 'nav.reports.hikvisionEvents',
    listEndpoint: 'getalleventosdata',
    listMethod: 'GET',
    codTabela: 'RELEVENTOSHIKVISION',
    needsDates: true,
    filters: [],
    filename: 'eventoshikvision',
    paramStyle: 'dti_dtf',
  },
  {
    id: 'controladores',
    screen: 'RELLancamentosControladores',
    labelKey: 'nav.reports.controllersTrips',
    listEndpoint: 'getallviagenscontroladoresdata',
    listMethod: 'GET',
    codTabela: 'RELLANCAMENTOSCONTROLADORES',
    needsDates: true,
    filters: [],
    filename: 'controladores',
    paramStyle: 'dti_dtf',
  },
  {
    id: 'veiculosKm',
    screen: 'RELVeiculosKm',
    labelKey: 'nav.reports.vehicleKm',
    listEndpoint: 'getveiculosrelatoriokm',
    listMethod: 'POST',
    needsDates: true,
    filters: ['veiculos'],
    filename: 'veiculoskm',
    paramStyle: 'veiculos_km',
  },
  {
    id: 'notificacoes',
    screen: 'RELNotificacoes',
    labelKey: 'nav.reports.notifications',
    listEndpoint: 'getnotificacoespordata',
    listMethod: 'POST',
    needsDates: true,
    filters: ['empresa', 'linhas', 'veiculos'],
    filename: 'notificacoes',
    paramStyle: 'notificacoes',
  },
  {
    id: 'statusVeiculos',
    screen: 'RELStatusVeiculos',
    labelKey: 'nav.reports.vehicleCommStatus',
    listEndpoint: 'getstatusallveiculospordata',
    listMethod: 'GET',
    codTabela: 'RELSTATUSVEICULOS',
    needsDates: true,
    filters: [],
    filename: 'statusveiculos',
    paramStyle: 'status_veiculos',
  },
  {
    id: 'operacao',
    screen: 'RELOperacao',
    labelKey: 'nav.reports.operacion',
    listEndpoint: 'getrelatoriooperacion',
    listMethod: 'POST',
    codTabela: 'RELOPERACAO',
    needsDates: true,
    filters: ['empresa', 'linhas', 'unidades'],
    filename: 'operacao',
    paramStyle: 'standard',
  },
  {
    id: 'passageirosLinhaDia',
    screen: 'RELPassageirosLinhaDia',
    labelKey: 'nav.reports.passengersByLineDay',
    listEndpoint: 'getrelatorioembarquesporlinha',
    listMethod: 'POST',
    needsDates: true,
    filters: ['linhas', 'unidades'],
    filename: 'passageiroslinhadia',
    paramStyle: 'passageiros_linha_dia',
  },
  {
    id: 'tempoDePlaca',
    screen: 'RELTempoDePlaca',
    labelKey: 'nav.reports.timeBetweenTrips',
    listEndpoint: 'getprevistorealizadolinhasdata',
    listMethod: 'POST',
    codTabela: 'RELTEMPODEPLACA',
    needsDates: true,
    filters: ['linhas', 'veiculos'],
    filename: 'tempodeplaca',
    paramStyle: 'cumprimento',
  },
  {
    id: 'tempoTrecho',
    screen: 'RELTempoTrecho',
    labelKey: 'nav.reports.stretchTime',
    listEndpoint: 'gettempotrecho',
    listMethod: 'POST',
    codTabela: 'RELTEMPOTRECHO',
    needsDates: true,
    filters: [],
    filename: 'tempotrecho',
    paramStyle: 'dti_dtf',
  },
  {
    id: 'viagensCerca',
    screen: 'RELViagensCerca',
    labelKey: 'nav.reports.tripsByGeofence',
    listEndpoint: 'getviagenscercainiciofim',
    listMethod: 'POST',
    needsDates: true,
    filters: ['cerca'],
    filename: 'viagenscerca',
    paramStyle: 'viagens_cerca',
  },
  {
    id: 'tempoPlacaServico',
    screen: 'RELTempoPlacaServico',
    labelKey: 'nav.reports.timeBetweenTripsByService',
    listEndpoint: 'gettempoplacatotalservico',
    listMethod: 'GET',
    codTabela: 'RELTEMPOPLACASERVICO',
    needsDates: true,
    filters: [],
    filename: 'tempoplacaservico',
    paramStyle: 'dti_dtf',
  },
  {
    id: 'velocidade',
    screen: 'RELVelocidade',
    labelKey: 'nav.reports.velocity',
    listEndpoint: 'getrelatoriovelocidade',
    listMethod: 'POST',
    needsDates: true,
    filters: ['linhas', 'veiculos', 'unidades'],
    filename: 'velocidade',
    paramStyle: 'velocidade',
  },
  {
    id: 'statusViagens',
    screen: 'RELStatusViagens',
    labelKey: 'nav.reports.tripStatus',
    listEndpoint: 'getagregadostatusviagens',
    listMethod: 'POST',
    needsDates: true,
    filters: ['empresa', 'linhas', 'unidades'],
    filename: 'statusviagens',
    paramStyle: 'status_viagens',
  },
  {
    id: 'embarquesTrecho',
    screen: 'RELEmbarquesTrecho',
    labelKey: 'nav.reports.boardingByStretch',
    listEndpoint: 'getembarquestrecho',
    listMethod: 'POST',
    needsDates: true,
    filters: [],
    filename: 'embarquestrecho',
    paramStyle: 'dti_dtf',
  },
  {
    id: 'comboio',
    screen: 'RELComboio',
    labelKey: 'nav.reports.convoy',
    listEndpoint: 'getveiculoscomboiolinhadata',
    listMethod: 'GET',
    needsDates: true,
    filters: ['empresa', 'linhas', 'unidades'],
    requiredFilters: ['linhas'],
    filename: 'relcomboio',
    paramStyle: 'comboio',
  },
  {
    id: 'consumoApiExterna',
    screen: 'RELConsumoApiExterna',
    labelKey: 'nav.reports.apiConsumption',
    listEndpoint: 'getrelatorioconsumoapiexternas',
    listMethod: 'POST',
    codTabela: 'RELCONSUMOAPI',
    needsDates: true,
    filters: [],
    filename: 'relconsumoapi',
    paramStyle: 'dti_dtf',
  },
  {
    id: 'historicoAlteracoesRotas',
    screen: 'RELHistoricoRotas',
    labelKey: 'nav.reports.routeChangeHistory',
    listEndpoint: 'gethistoricoalteracoesrotas',
    listMethod: 'GET',
    codTabela: 'RELHISTORICOALTERACOESROTAS',
    needsDates: true,
    filters: [],
    filename: 'historicoalteracoesrotas',
    paramStyle: 'dti_dtf_date',
  },
  {
    id: 'historicoEquipamentos',
    screen: 'RELManutencaoHistorico',
    labelKey: 'nav.reports.equipHistory',
    listEndpoint: 'gethistoricoveiculoequipamento',
    listMethod: 'POST',
    needsDates: true,
    filters: ['empresa', 'veiculos', 'unidades'],
    requiredFilters: ['veiculos'],
    filename: 'historicomanutencao',
    paramStyle: 'historico_equipamentos',
  },
  {
    id: 'historicoRoteirizacao',
    screen: 'RELHistoricoRoteirizacao',
    labelKey: 'nav.reports.routingHistoryTimeline',
    listEndpoint: 'gethistoricoroteirizacaosessoes',
    listMethod: 'GET',
    codTabela: 'RELHISTORICOROTEIRIZACAO',
    needsDates: true,
    filters: [],
    filename: 'historicoroteirizacao',
    paramStyle: 'dti_dtf_date',
  },
  {
    id: 'historicoVeiculo',
    screen: 'RELHistoricoVeiculo',
    labelKey: 'nav.reports.vehicleHistory',
    listEndpoint: 'getviagensgridveiculodata',
    listMethod: 'POST',
    codTabela: 'RELHISTORICOVEICULO__VIAGENS',
    needsDates: true,
    filters: ['empresa', 'veiculos', 'unidades'],
    requiredFilters: ['veiculos'],
    filename: 'historicoveiculo_viagens',
    paramStyle: 'historico_veiculo',
    tableVariants: HISTORICO_VEICULO_TABLE_VARIANTS,
  },
  {
    id: 'kmTempoReal',
    screen: 'RELKmTempoReal',
    labelKey: 'nav.reports.realTimeKm',
    listEndpoint: 'getveiculosrelatoriokmtemporeal',
    listMethod: 'POST',
    codTabela: 'RELKMTEMPOREAL__KM_TEMPO_REAL_WITH_PROPERTIES',
    needsDates: false,
    filters: ['empresa', 'veiculos', 'unidades'],
    filename: 'relkmtemporeal',
    paramStyle: 'km_tempo_real',
  },
  {
    id: 'manutencaoEquipamentos',
    screen: 'RELManutencao',
    labelKey: 'nav.reports.equipMaintenance',
    listEndpoint: 'getequipamentosrastreadores',
    listMethod: 'POST',
    needsDates: true,
    filters: ['empresa', 'veiculos', 'unidades'],
    filename: 'relmanutencao',
    paramStyle: 'manutencao',
    tableVariants: MANUTENCAO_EQUIPAMENTOS_TABLE_VARIANTS,
  },
  {
    id: 'passageirosViagem',
    screen: 'RELPassageirosViagem',
    labelKey: 'nav.reports.passengers',
    listEndpoint: 'getpassageirosporfaixahoraria',
    listMethod: 'POST',
    codTabela: 'RELPASSAGEIROSVIAGEM__FAIXAS_WITH_PROPERTIES',
    needsDates: true,
    filters: ['empresa', 'linhas', 'veiculos', 'unidades', 'intervalo'],
    filename: 'passageirosporfaixahoraria',
    paramStyle: 'passageiros_faixas',
    tableVariants: PASSAGEIROS_VIAGEM_TABLE_VARIANTS,
  },
  {
    id: 'previstoRealizado',
    screen: 'RELPrevistoRealizado',
    labelKey: 'nav.reports.timetableFulfillment',
    listEndpoint: 'getprevistorealizadolinhasdata',
    listMethod: 'POST',
    codTabela: 'RELPREVREAL',
    needsDates: true,
    filters: ['empresa', 'linhas', 'veiculos', 'unidades'],
    filename: 'relprevistorealizado',
    paramStyle: 'previsto_realizado',
  },
];

/**
 * @param {object} ctx
 * @param {RelatorioExtensoEntry} entry
 */
export function buildRelatorioExtensoRequestParams(entry, ctx) {
  const style = entry.paramStyle || 'standard';
  const dti = ctx.dtiSql || null;
  const dtf = ctx.dtfSql || null;
  const empresa = ctx.empresa || null;
  const linhas = ctx.linhas && ctx.linhas.length ? ctx.linhas : null;
  const veiculos = ctx.veiculos && ctx.veiculos.length ? ctx.veiculos : null;
  const unidades = ctx.unidades && ctx.unidades.length ? ctx.unidades : null;

  switch (style) {
    case 'empty':
      return { dti, dtf };
    case 'acesso_qrcode':
      return {};
    case 'usuarios_passageiros':
      return { dti, dtf };
    case 'status_veiculos':
      return { dt: dtf || dti };
    case 'dti_dtf':
      return { dti, dtf };
    case 'viagens_cerca':
      return {
        dti,
        dtf,
        idcerca: ctx.cerca || null,
      };
    case 'diario':
      return {
        dti,
        dtf,
        idlinhas: linhas,
        idveiculos: veiculos,
      };
    case 'embarques':
      return {
        dti,
        dtf,
        id_linha: linhas,
        id_veiculo: veiculos,
        id_unidade: unidades,
      };
    case 'ocupacao':
      return {
        dti,
        dtf,
        id_linhas: linhas,
        id_unidades: unidades,
      };
    case 'cumprimento':
      return {
        dti,
        dtf,
        idlinhas: linhas,
        idveiculos: veiculos,
        idfuncionarios: null,
        idempresa: empresa,
        idunidades: unidades || [],
        idcliente: null,
      };
    case 'veiculos_km':
      return {
        dti,
        dtf,
        ids: veiculos,
      };
    case 'notificacoes':
      return {
        dtinicio: dti,
        dtfim: dtf,
        idempresa: empresa,
        idlinha: linhas && linhas.length === 1 ? linhas[0] : null,
        idveiculo: veiculos && veiculos.length ? veiculos : null,
        tiponotificacao: null,
        tratada: null,
        bilhetagemfora: null,
      };
    case 'passageiros_linha_dia': {
      const body = {
        dti,
        dtf,
        intervalo: 'DAY',
      };
      if (linhas && linhas.length) body.id_linhas = linhas.map((id) => Number(id));
      if (unidades && unidades.length) body.id_unidades = unidades.map((id) => Number(id));
      return body;
    }
    case 'velocidade':
      return {
        dti,
        dtf,
        idlinhas: linhas,
        idveiculos: veiculos,
        idcercas: null,
      };
    case 'status_viagens':
      return {
        dti,
        dtf,
        idlinhas: linhas && linhas.length ? linhas : null,
      };
    case 'dti_dtf_date': {
      const dateOnly = (v) => (v ? String(v).slice(0, 10) : null);
      return { dti: dateOnly(dti), dtf: dateOnly(dtf) };
    }
    case 'comboio':
      return {
        idlinha: linhas && linhas.length ? linhas[0] : null,
        dti,
        dtf,
      };
    case 'historico_equipamentos':
      return {
        ids: veiculos && veiculos.length ? veiculos : [],
        dti,
        dtf,
      };
    case 'historico_veiculo':
      return {
        idveiculo: veiculos && veiculos.length ? veiculos[0] : null,
        dti,
        dtf,
      };
    case 'km_tempo_real':
      return {
        ids: veiculos && veiculos.length ? veiculos : [],
      };
    case 'manutencao':
      return {
        ids: veiculos && veiculos.length ? veiculos : [],
        dti,
        dtf,
        param: 'veiculo',
      };
    case 'passageiros_faixas':
      return {
        dti,
        dtf,
        intervalo: ctx.intervalo || 'DAY',
        idlinhas: linhas && linhas.length ? linhas : null,
        idveiculos: veiculos && veiculos.length ? veiculos : null,
      };
    case 'previsto_realizado': {
      const hora = ctx.config && ctx.config.hora_inicial_dia_operacao
        ? String(ctx.config.hora_inicial_dia_operacao).replace(/:/g, '')
        : null;
      return {
        id_linha: linhas && linhas.length ? linhas : null,
        id_veiculo: veiculos && veiculos.length ? veiculos : null,
        dti,
        dtf,
        inicio_operacional: hora ? `${hora}00` : null,
      };
    }
    case 'standard_grupo':
      return {
        dti,
        dtf,
        id_empresa: empresa,
        id_linhas: linhas,
        id_veiculos: veiculos,
        id_unidades: unidades,
        grupo: ctx.grupo || 'veiculo',
      };
    case 'standard':
    default:
      return {
        dti,
        dtf,
        id_empresa: empresa,
        id_linhas: linhas,
        id_veiculos: veiculos,
        id_unidades: unidades,
      };
  }
}

export function getRelatorioExtensoById(id) {
  return RELATORIOS_EXTENSO_CATALOG.find((e) => e.id === id) || null;
}

/**
 * @param {RelatorioExtensoEntry|null|undefined} entry
 * @param {string|null|undefined} variantId
 * @returns {RelatorioExtensoTableVariant|null}
 */
export function getRelatorioExtensoTableVariant(entry, variantId) {
  const variants = entry && Array.isArray(entry.tableVariants) ? entry.tableVariants : [];
  if (!variants.length) return null;
  if (variantId) {
    const found = variants.find((v) => v.id === variantId);
    if (found) return found;
  }
  return variants[0];
}
